import { Slot } from '@radix-ui/react-slot';
import { type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Inline } from '../inline/inline';
import { buttonVariants } from './button-variants';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    /** When true, the component will render its children directly without wrapping them in a button element */
    asChild?: boolean;
    /** Icon element to display on the left side of the button content */
    iconLeft?: ReactNode;
    /** Icon element to display on the right side of the button content */
    iconRight?: ReactNode;
    /** Optional class name to apply to the right icon's wrapper */
    iconRightClassName?: string;
    rounded?: boolean;
    /** When true, displays a loading spinner and disables the button */
    isLoading?: boolean;
  };

/**
 * A versatile button component that supports various styles and configurations.
 *
 * @component
 * @example
 * ```tsx
 * <Button variant="primary" size="md" iconLeft={<Icon />}>
 *   Click me
 * </Button>
 * ```
 *
 * @param {ButtonProps} props - The component props
 * @param {string} [props.variant] - Visual style variant of the button
 * @param {string} [props.size] - Size variant of the button
 * @param {boolean} [props.asChild=false] - When true, renders children directly without button wrapper
 * @param {ReactNode} [props.iconLeft] - Icon element to display on the left
 * @param {ReactNode} [props.iconRight] - Icon element to display on the right
 * @param {boolean} [props.rounded=false] - When true, applies rounded corners to the button. Useful for ghost buttons, in case you want to remove the rounded corners.
 * @param {ReactNode} props.children - Button content
 */

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      iconLeft,
      iconRight,
      children,
      rounded,
      isLoading = false,
      disabled,
      iconRightClassName,
      ...props
    },
    ref,
  ) => {
    const Component = asChild ? Slot : 'button';

    return (
      <Component
        className={cn(buttonVariants({ variant, size, className, rounded }), 'flex min-w-0 items-center gap-2')}
        ref={ref}
        disabled={disabled || isLoading}
        data-is-loading={isLoading}
        {...props}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin" />
        ) : (
          iconLeft && <div className="flex-shrink-0">{iconLeft}</div>
        )}

        {variant === 'ghost' ? (
          <Inline justify="center" className="w-full self-stretch">
            {children}
          </Inline>
        ) : (
          <div className="min-w-0 truncate">{children}</div>
        )}

        {iconRight && <div className={cn('flex-shrink-0', iconRightClassName)}>{iconRight}</div>}
      </Component>
    );
  },
);
